// Centralized acronym casing for user-facing strings.
//
// The DB stores topic labels as title-cased strings derived from slugs
// (e.g. "amazon-ppc" → "Amazon Ppc"). When we render those labels in the UI,
// we want common ecommerce acronyms in their canonical ALL-CAPS form.
//
// Usage: <h1>{fixAcronyms(topic.label)}</h1>

const ACRONYMS = [
  "PPC", "SEO", "DTC", "D2C", "B2B", "B2C", "ROAS", "ROI", "AOV", "LTV",
  "CAC", "AMC", "SMS", "FBA", "AI", "UGC", "CRO", "KPI", "API", "URL",
  "FAQ", "CRM", "ERP", "CMS", "OOS", "MOQ", "SKU", "USP", "UX", "UI",
  "TOFU", "MOFU", "BOFU", "SaaS", "P&L", "GTM", "PR", "QR", "EU", "US",
  "UK", "AR", "VR", "NPS", "CSAT", "CTA", "CTR", "CPC", "CPM", "CPA",
  "RFM", "PDP", "PLP", "BFCM",
];

// Build a single regex that matches any acronym as a whole word, case-insensitive.
// We match against word boundaries that allow letters/digits on either side.
const ACRONYM_PATTERN = new RegExp(
  `\\b(${ACRONYMS.map((a) => a.replace(/[&]/g, "\\$&")).join("|")})\\b`,
  "gi",
);

const CANONICAL = new Map(ACRONYMS.map((a) => [a.toLowerCase(), a]));

/**
 * Replace any occurrence of a known acronym (case-insensitive) with its
 * canonical capitalization. Leaves all other text untouched.
 *
 * Examples:
 *   "Amazon Ppc Fundamentals"  → "Amazon PPC Fundamentals"
 *   "seo for ecommerce"        → "SEO for ecommerce"
 *   "Roas Optimization"        → "ROAS Optimization"
 */
export function fixAcronyms(input: string | null | undefined): string {
  if (!input) return "";
  return input.replace(ACRONYM_PATTERN, (m) => CANONICAL.get(m.toLowerCase()) ?? m);
}

/**
 * Lowercase a string while keeping known acronyms in their canonical form.
 * Useful inside running prose like "Find the best episodes about amazon PPC →".
 */
export function lowerWithAcronyms(input: string | null | undefined): string {
  if (!input) return "";
  return fixAcronyms(input.toLowerCase());
}
